import Swal from 'sweetalert2';

const DISABLED_CONFIG = {
    enabled: false,
    parentMultiMapPublicId: '',
    rvChildMapPublicId: '',
    stallsChildMapPublicId: '',
};

const PROMPT_MESSAGE = 'Do you want to rent stalls with your order?';
const PROMPT_CONFIRM_BUTTON = 'Yes';
const PROMPT_CANCEL_BUTTON = 'No';
const HANDLED_FLAG_SUFFIX = ':handled';
const OPENED_STALLS_FLAG_SUFFIX = ':opened_stalls';

/**
 * @param {*} value
 * @return {string}
 */
function normalizePublicIdValue(value) {
    return typeof value === 'string'
        ? value.trim()
        : '';
}

/**
 * @param {*} config
 * @return {{enabled: boolean, parentMultiMapPublicId: string, rvChildMapPublicId: string, stallsChildMapPublicId: string}}
 */
function normalizeRvStallUpsellConfig(config) {
    if (!config || typeof config !== 'object') {
        return {...DISABLED_CONFIG};
    }

    const parentMultiMapPublicId = normalizePublicIdValue(config.parentMultiMapPublicId);
    const rvChildMapPublicId = normalizePublicIdValue(config.rvChildMapPublicId);
    const stallsChildMapPublicId = normalizePublicIdValue(config.stallsChildMapPublicId);

    if (config.enabled !== true || !parentMultiMapPublicId || !rvChildMapPublicId || !stallsChildMapPublicId) {
        return {...DISABLED_CONFIG};
    }

    return {
        enabled: true,
        parentMultiMapPublicId,
        rvChildMapPublicId,
        stallsChildMapPublicId,
    };
}

/**
 * @param {{enabled: boolean, parentMultiMapPublicId: string}} config
 * @param {string|null|undefined} currentParentMapPublicId
 * @return {boolean}
 */
function isCurrentParentMap(config, currentParentMapPublicId) {
    return config.enabled === true
        && normalizePublicIdValue(currentParentMapPublicId) === config.parentMultiMapPublicId;
}

/**
 * @param {*} globalState
 * @param {string} seatMapPublicId
 * @return {boolean}
 */
function hasSelectedSeatsForSeatMap(globalState, seatMapPublicId) {
    const selectedSeats = Array.isArray(globalState?.selectedSeats)
        ? globalState.selectedSeats
        : [];

    return selectedSeats.some(seat => normalizePublicIdValue(seat?.seat_map_id) === seatMapPublicId);
}

/**
 * @param {*} globalState
 * @param {{rvChildMapPublicId: string}} config
 * @return {boolean}
 */
function hasSelectedRvSeats(globalState, config) {
    return hasSelectedSeatsForSeatMap(globalState, config.rvChildMapPublicId);
}

/**
 * @param {*} globalState
 * @param {{stallsChildMapPublicId: string}} config
 * @return {boolean}
 */
function hasSelectedStallsSeats(globalState, config) {
    return hasSelectedSeatsForSeatMap(globalState, config.stallsChildMapPublicId);
}

/**
 * @param {*} record
 * @return {string}
 */
function getRecordContextId(record) {
    if (!record || typeof record !== 'object') {
        return 'none';
    }

    return normalizePublicIdValue(record.public_id)
        || normalizePublicIdValue(record.publicId)
        || normalizePublicIdValue(record.id)
        || 'none';
}

/**
 * @param {*} globalState
 * @param {string} parentMapPublicId
 * @return {string}
 */
function buildRvStallUpsellSessionKey(globalState, parentMapPublicId) {
    const parentEventId = getRecordContextId(globalState?.parentEvent);
    const eventDateId = getRecordContextId(globalState?.selectedDate);
    const normalizedParentMapPublicId = normalizePublicIdValue(parentMapPublicId) || 'none';

    return `rv_stall_upsell:${parentEventId}:${eventDateId}:${normalizedParentMapPublicId}`;
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @return {boolean|null}
 */
function readSessionFlag(storage, key) {
    try {
        return storage.getItem(key) === '1';
    } catch (error) {
        return null;
    }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @return {boolean}
 */
function writeSessionFlag(storage, key) {
    try {
        storage.setItem(key, '1');

        return true;
    } catch (error) {
        return false;
    }
}

/**
 * @param {Storage} storage
 * @param {string} sessionKey
 * @return {boolean|null}
 */
function hasHandledRvStallUpsell(storage, sessionKey) {
    return readSessionFlag(storage, `${sessionKey}${HANDLED_FLAG_SUFFIX}`);
}

/**
 * @param {Storage} storage
 * @param {string} sessionKey
 * @return {boolean}
 */
function markRvStallUpsellHandled(storage, sessionKey) {
    return writeSessionFlag(storage, `${sessionKey}${HANDLED_FLAG_SUFFIX}`);
}

/**
 * @param {Storage} storage
 * @param {string} sessionKey
 * @return {boolean|null}
 */
function hasOpenedStallsForRvStallUpsell(storage, sessionKey) {
    return readSessionFlag(storage, `${sessionKey}${OPENED_STALLS_FLAG_SUFFIX}`);
}

/**
 * @param {Storage} storage
 * @param {string} sessionKey
 * @return {boolean}
 */
function markOpenedStallsForRvStallUpsell(storage, sessionKey) {
    return writeSessionFlag(storage, `${sessionKey}${OPENED_STALLS_FLAG_SUFFIX}`);
}

/**
 * @param {{globalState: *, currentParentMapPublicId: string, storage?: Storage}} options
 * @return {{shouldIntercept: boolean, sessionKey: string|null, config: {enabled: boolean, parentMultiMapPublicId: string, rvChildMapPublicId: string, stallsChildMapPublicId: string}}}
 */
function shouldInterceptRvStallUpsellContinue(options) {
    const globalState = options?.globalState;
    const config = normalizeRvStallUpsellConfig(globalState?.publicTicketConfig?.rvStallUpsell);
    const currentParentMapPublicId = typeof options?.getCurrentParentMapPublicId === 'function'
        ? options.getCurrentParentMapPublicId()
        : options?.currentParentMapPublicId;

    if (!isCurrentParentMap(config, currentParentMapPublicId)) {
        return {
            shouldIntercept: false,
            sessionKey: null,
            config,
        };
    }

    if (!hasSelectedRvSeats(globalState, config) || hasSelectedStallsSeats(globalState, config)) {
        return {
            shouldIntercept: false,
            sessionKey: null,
            config,
        };
    }

    const storage = options?.storage || window.sessionStorage;
    const sessionKey = buildRvStallUpsellSessionKey(globalState, config.parentMultiMapPublicId);
    const openedStalls = hasOpenedStallsForRvStallUpsell(storage, sessionKey);
    const handled = hasHandledRvStallUpsell(storage, sessionKey);

    if (openedStalls === null || handled === null || openedStalls || handled) {
        return {
            shouldIntercept: false,
            sessionKey,
            config,
        };
    }

    return {
        shouldIntercept: true,
        sessionKey,
        config,
    };
}

/**
 * @return {Promise<'accepted'|'declined'>}
 */
function showRvStallUpsellPrompt() {
    return Swal.fire({
        text: PROMPT_MESSAGE,
        showCancelButton: true,
        confirmButtonText: PROMPT_CONFIRM_BUTTON,
        cancelButtonText: PROMPT_CANCEL_BUTTON,
    }).then(result => {
        return result?.isConfirmed === true || result?.value === true
            ? 'accepted'
            : 'declined';
    });
}

/**
 * @param {*} options
 * @return {{shouldInterceptContinue: function(): {shouldIntercept: boolean, sessionKey: string|null, config: {enabled: boolean, parentMultiMapPublicId: string, rvChildMapPublicId: string, stallsChildMapPublicId: string}}, showPrompt: function(): Promise<'accepted'|'declined'>, markMapActivated: function(*, *=): void, destroy: function(): void}}
 */
function initializeRvStallUpsell(options = {}) {
    const storage = options?.storage || window.sessionStorage;
    const controller = {
        shouldInterceptContinue() {
            return shouldInterceptRvStallUpsellContinue(options);
        },
        showPrompt() {
            return showRvStallUpsellPrompt();
        },
        markMapActivated(mapState, activationContext = {}) {
            if (activationContext.userTriggered !== true) {
                return;
            }

            const config = normalizeRvStallUpsellConfig(options?.globalState?.publicTicketConfig?.rvStallUpsell);
            const currentParentMapPublicId = typeof options?.getCurrentParentMapPublicId === 'function'
                ? options.getCurrentParentMapPublicId()
                : options?.currentParentMapPublicId;

            if (!isCurrentParentMap(config, currentParentMapPublicId) || mapState?.id !== config.stallsChildMapPublicId) {
                return;
            }

            markOpenedStallsForRvStallUpsell(
                storage,
                buildRvStallUpsellSessionKey(options.globalState, config.parentMultiMapPublicId),
            );
        },
        handleContinue(event) {
            const decision = controller.shouldInterceptContinue();

            if (decision.shouldIntercept === false) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            controller.showPrompt()
                .then(result => {
                    if (decision.sessionKey) {
                        markRvStallUpsellHandled(storage, decision.sessionKey);
                    }

                    if (result === 'accepted') {
                        const stallsMapState = Array.isArray(options?.mapStates)
                            ? options.mapStates.find(mapState => mapState.id === decision.config.stallsChildMapPublicId)
                            : null;

                        if (stallsMapState && typeof options?.activateMapState === 'function') {
                            options.activateMapState(stallsMapState, {userTriggered: true});
                        }

                        return;
                    }

                    if (typeof options?.nextStage === 'function') {
                        options.nextStage();
                    }
                });
        },
        destroy() {
            if (options?.nextButton instanceof HTMLElement) {
                options.nextButton.removeEventListener('click', controller.handleContinue, true);
            }
        },
    };

    if (options?.nextButton instanceof HTMLElement) {
        options.nextButton.addEventListener('click', controller.handleContinue, true);
    }

    return controller;
}

export {
    PROMPT_CANCEL_BUTTON,
    PROMPT_CONFIRM_BUTTON,
    PROMPT_MESSAGE,
    buildRvStallUpsellSessionKey,
    hasHandledRvStallUpsell,
    hasOpenedStallsForRvStallUpsell,
    hasSelectedRvSeats,
    hasSelectedSeatsForSeatMap,
    hasSelectedStallsSeats,
    initializeRvStallUpsell,
    isCurrentParentMap,
    markOpenedStallsForRvStallUpsell,
    markRvStallUpsellHandled,
    normalizeRvStallUpsellConfig,
    shouldInterceptRvStallUpsellContinue,
    showRvStallUpsellPrompt,
};
